// 歌单工具函数

import {
  BpmMatchLevel,
  KeyCompatibility,
  MusicalKey,
  KeyMode,
  PlaylistTrack,
  TrackRecommendation,
  FilterCriteria,
  PlaylistStats,
  PlaylistItem,
  MusicGenre,
} from '@/types/playlist'

// 计算BPM匹配度
export function calculateBpmMatch(bpm1: number, bpm2: number): BpmMatchLevel {
  const diff = Math.abs(bpm1 - bpm2)
  
  if (diff <= 3) return 'perfect'
  if (diff <= 8) return 'good'
  if (diff <= 15) return 'fair'
  return 'poor'
}

// 获取BPM匹配度颜色
export function getBpmMatchColor(level: BpmMatchLevel): string {
  const colors = {
    perfect: '#10b981', // green-500
    good: '#3b82f6', // blue-500
    fair: '#f59e0b', // amber-500
    poor: '#ef4444', // red-500
  }
  return colors[level]
}

// 调性圈 - Camelot Wheel映射
const camelotWheel: Record<string, number> = {
  'C-major': 8,
  'C-minor': 5,
  'C#-major': 3,
  'C#-minor': 12,
  'D-major': 10,
  'D-minor': 7,
  'D#-major': 5,
  'D#-minor': 2,
  'E-major': 12,
  'E-minor': 9,
  'F-major': 7,
  'F-minor': 4,
  'F#-major': 2,
  'F#-minor': 11,
  'G-major': 9,
  'G-minor': 6,
  'G#-major': 4,
  'G#-minor': 1,
  'A-major': 11,
  'A-minor': 8,
  'A#-major': 6,
  'A#-minor': 3,
  'B-major': 1,
  'B-minor': 10,
}

// 将调性转换为 Camelot 格式（1-12A/B）
export function toCamelotKey(key: MusicalKey, mode: KeyMode): string {
  const camelotNumber = camelotWheel[`${key}-${mode}`]
  if (!camelotNumber) return `${key} ${mode === 'major' ? 'Major' : 'Minor'}`
  
  // B 代表 Major (外圈), A 代表 Minor (内圈)
  const camelotLetter = mode === 'major' ? 'B' : 'A'
  return `${camelotNumber}${camelotLetter}`
}

// 计算调性兼容性
export function calculateKeyCompatibility(
  key1: MusicalKey,
  mode1: KeyMode,
  key2: MusicalKey,
  mode2: KeyMode
): KeyCompatibility {
  const camelot1 = camelotWheel[`${key1}-${mode1}`]
  const camelot2 = camelotWheel[`${key2}-${mode2}`]
  
  if (!camelot1 || !camelot2) return 'poor'
  
  const diff = Math.min(
    Math.abs(camelot1 - camelot2),
    12 - Math.abs(camelot1 - camelot2)
  )
  
  // 相同调性
  if (key1 === key2 && mode1 === mode2) return 'perfect'
  
  // Camelot相邻（±1）
  if (diff === 1) return 'good'
  
  // 同主音异模式或五度关系
  if (diff <= 2) return 'moderate'
  
  return 'poor'
}

// 获取调性兼容度颜色
export function getKeyCompatibilityColor(level: KeyCompatibility): string {
  const colors = {
    perfect: '#10b981',
    good: '#3b82f6',
    moderate: '#f59e0b',
    poor: '#ef4444',
  }
  return colors[level]
}

// 计算风格相似度
export function calculateGenreSimilarity(genre1: string, genre2: string): number {
  if (genre1 === genre2) return 100
  
  // 电子音乐组
  const electronicGroup = ['House', 'Techno', 'Trance', 'EDM', 'Dubstep', 'Drum & Bass', 'Electronic']
  // 流行/摇滚组
  const popRockGroup = ['Pop', 'Rock', 'Hip-Hop', 'Hip Hop']
  // 灵魂乐/放克组
  const soulFunkGroup = ['Funk / Soul', 'Soul', 'Funk', 'R&B']
  
  if (electronicGroup.includes(genre1) && electronicGroup.includes(genre2)) return 70
  if (popRockGroup.includes(genre1) && popRockGroup.includes(genre2)) return 70
  if (soulFunkGroup.includes(genre1) && soulFunkGroup.includes(genre2)) return 70
  
  return 30
}

// 计算能量曲线匹配度
export function calculateEnergyCurveMatch(
  currentEnergy: number,
  nextEnergy: number,
  desiredCurve: 'ascending' | 'descending' | 'balanced' = 'balanced'
): number {
  const diff = nextEnergy - currentEnergy
  
  switch (desiredCurve) {
    case 'ascending':
      // 希望能量上升
      if (diff > 10) return 100
      if (diff > 0) return 80
      if (diff > -5) return 60
      return 40
    case 'descending':
      // 希望能量下降
      if (diff < -10) return 100
      if (diff < 0) return 80
      if (diff < 5) return 60
      return 40
    case 'balanced':
      // 希望能量相近
      if (Math.abs(diff) < 5) return 100
      if (Math.abs(diff) < 10) return 80
      if (Math.abs(diff) < 20) return 60
      return 40
  }
}

// 生成歌曲推荐
export function generateRecommendations(
  currentTrack: PlaylistTrack,
  availableTracks: PlaylistTrack[],
  filter?: FilterCriteria
): TrackRecommendation[] {
  let candidates = availableTracks.filter(t => t.id !== currentTrack.id)
  
  // 应用过滤条件
  if (filter) {
    if (filter.bpmRange) {
      candidates = candidates.filter(
        t => t.features.bpm >= filter.bpmRange![0] && 
             t.features.bpm <= filter.bpmRange![1]
      )
    }
    if (filter.genres && filter.genres.length > 0) {
      candidates = candidates.filter(t => filter.genres!.includes(t.genre as any))
    }
    if (filter.energyRange) {
      candidates = candidates.filter(
        t => t.features.energy >= filter.energyRange![0] && 
             t.features.energy <= filter.energyRange![1]
      )
    }
    if (filter.keys && filter.keys.length > 0) {
      candidates = candidates.filter(t => filter.keys!.includes(t.features.key))
    }
    if (filter.keyModes && filter.keyModes.length > 0) {
      candidates = candidates.filter(t => filter.keyModes!.includes(t.features.keyMode))
    }
  }
  
  // 为每个候选歌曲计算推荐分数
  const recommendations: TrackRecommendation[] = candidates.map(track => {
    const bpmMatch = calculateBpmMatch(currentTrack.features.bpm, track.features.bpm)
    const keyCompatibility = calculateKeyCompatibility(
      currentTrack.features.key,
      currentTrack.features.keyMode,
      track.features.key,
      track.features.keyMode
    )
    const genreSimilarity = calculateGenreSimilarity(currentTrack.genre, track.genre)
    const energyCurve = calculateEnergyCurveMatch(
      currentTrack.features.energy,
      track.features.energy
    )
    
    // 计算总分
    const bpmScore = { perfect: 30, good: 22, fair: 12, poor: 5 }[bpmMatch]
    const keyScore = { perfect: 25, good: 18, moderate: 10, poor: 3 }[keyCompatibility]
    const genreScore = genreSimilarity * 0.25
    const energyScore = energyCurve * 0.20
    
    const totalScore = bpmScore + keyScore + genreScore + energyScore
    
    return {
      track,
      score: Math.round(totalScore),
      reasons: {
        bpmMatch,
        keyCompatibility,
        genreSimilarity,
        energyCurve,
      },
    }
  })
  
  // 按分数排序
  return recommendations.sort((a, b) => b.score - a.score)
}

// 计算歌单统计信息
export function calculatePlaylistStats(items: PlaylistItem[]): PlaylistStats {
  if (items.length === 0) {
    return {
      totalTracks: 0,
      totalDuration: 0,
      averageBpm: 0,
      bpmRange: [0, 0],
      genreDistribution: {},
      energyCurve: [],
      keyDistribution: {} as Record<MusicalKey, number>,
    }
  }
  
  const tracks = items.map(item => item.track)
  
  const totalDuration = tracks.reduce((sum, t) => sum + t.duration, 0)
  const bpms = tracks.map(t => t.features.bpm)
  const averageBpm = Math.round(bpms.reduce((sum, bpm) => sum + bpm, 0) / bpms.length)
  const bpmRange: [number, number] = [Math.min(...bpms), Math.max(...bpms)]
  
  const genreDistribution: Record<string, number> = {}
  tracks.forEach(t => {
    genreDistribution[t.genre] = (genreDistribution[t.genre] || 0) + 1
  })
  
  const energyCurve = tracks.map(t => t.features.energy)
  
  const keyDistribution: Record<MusicalKey, number> = {} as Record<MusicalKey, number>
  tracks.forEach(t => {
    keyDistribution[t.features.key] = (keyDistribution[t.features.key] || 0) + 1
  })
  
  return {
    totalTracks: tracks.length,
    totalDuration,
    averageBpm,
    bpmRange,
    genreDistribution,
    energyCurve,
    keyDistribution,
  }
}

// 格式化时长
export function formatDuration(seconds: number): string {
  const mins = Math.floor(seconds / 60)
  const secs = Math.floor(seconds % 60)
  return `${mins}:${secs.toString().padStart(2, '0')}`
}

// 格式化总时长
export function formatTotalDuration(seconds: number): string {
  const hours = Math.floor(seconds / 3600)
  const mins = Math.floor((seconds % 3600) / 60)
  
  if (hours > 0) {
    return `${hours}小时 ${mins}分钟`
  }
  return `${mins}分钟`
}

